/**
 * @fileoverview Flagged Transactions — agent copy renderer with statute badges
 * @module jobs/phases/review/flaggedTransactions/AgentText
 *
 * Renders practitioner-facing agent text (assessments, callouts, reasoning)
 * through `agentText` cleanup, then highlights legislation references
 * (s588FA, ss 588FE(2), section 596AB, "Corporations Act 2001 (Cth)") as
 * inline badges so the statutory hooks stand out from the prose.
 */

import { Fragment, type ReactNode } from "react";
import { cn } from "@/utils/misc";
import { agentText } from "./flaggedShared";

/**
 * Matches Australian statute references in agent prose:
 *   s588FA · s 588FE(2)(b) · ss588FA–588FG · section 596AB · Pt 5.7B
 *   Corporations Act 2001 (Cth) — with or without the year/jurisdiction.
 */
const STATUTE_REGEX =
  /(\bss?\s?\.?\s?\d{2,4}[A-Z]{0,3}(?:\([0-9A-Za-z]+\))*(?:\s?[–-]\s?\d{2,4}[A-Z]{0,3}(?:\([0-9A-Za-z]+\))*)?|\b[Ss]ections?\s\d{2,4}[A-Z]{0,3}(?:\([0-9A-Za-z]+\))*|\bPt\s?\d+\.\d+[A-Z]{0,2}\b|\bCorporations\sAct(?:\s2001)?(?:\s\(Cth\))?)/g;

export function StatuteBadge({ children }: { children: ReactNode }) {
  return (
    <span className="mx-px inline-flex items-center rounded bg-indigo-50 px-1 font-medium text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
      {children}
    </span>
  );
}

/** Splits cleaned text on statute references and wraps each in a badge. */
function renderWithStatutes(text: string): ReactNode {
  const parts = text.split(STATUTE_REGEX);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <StatuteBadge key={i}>{part}</StatuteBadge>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

/**
 * Agent prose paragraph: escape-sequence cleanup + ISO-date humanising via
 * `agentText`, statute references rendered as badges. Inline (renders a
 * `<span>`) — use for single values, headlines, and one-line notes.
 */
export function AgentText(props: { text: string; className?: string }) {
  const { text, className } = props;
  return (
    <span className={cn("whitespace-pre-line", className)}>
      {renderWithStatutes(agentText(text))}
    </span>
  );
}

/** A leading "- ", "* ", or "• " marks a bullet line. */
const BULLET_RE = /^\s*[-*•]\s+/;

type ProseBlock =
  { type: "p"; lines: string[] } | { type: "ul"; items: string[] };

/**
 * Group cleaned agent text into paragraph and bullet-list blocks so the agent's
 * "lead sentence + bullets" format renders as a real list instead of a wall of
 * text. Consecutive bullet lines collapse into one `<ul>`; everything else is a
 * paragraph.
 */
function toProseBlocks(cleaned: string): ProseBlock[] {
  const blocks: ProseBlock[] = [];
  // A blank line marks a paragraph break, so the next line starts a fresh
  // paragraph block (rendered with spacing) rather than merging into the last.
  cleaned.split("\n").reduce((breakParagraph, line) => {
    if (BULLET_RE.test(line)) {
      const item = line.replace(BULLET_RE, "").trim();
      const last = blocks[blocks.length - 1];
      if (last && last.type === "ul") last.items.push(item);
      else blocks.push({ type: "ul", items: [item] });
      return false;
    }
    if (line.trim() === "") return true;
    const last = blocks[blocks.length - 1];
    if (!breakParagraph && last && last.type === "p") last.lines.push(line);
    else blocks.push({ type: "p", lines: [line] });
    return false;
  }, false);
  return blocks;
}

/**
 * Block-level agent prose renderer: same cleanup + statute badges as
 * `AgentText`, but renders bullet lines ("- ...") as a proper `<ul>` and other
 * lines as paragraphs. Use for multi-sentence fields (reasoning, description)
 * so they read as a lead sentence with broken-up bullets rather than one
 * paragraph. Renders a `<div>`, so its parent must be a block element (not a
 * `<p>`).
 */
export function AgentProse(props: { text: string; className?: string }) {
  const { text, className } = props;
  const blocks = toProseBlocks(agentText(text));
  return (
    <div className={cn("space-y-1.5", className)}>
      {blocks.map((block, i) =>
        block.type === "ul" ? (
          <ul
            key={i}
            className="list-disc space-y-1 pl-4 marker:text-muted-foreground/60"
          >
            {block.items.map((item, j) => (
              <li key={j}>{renderWithStatutes(item)}</li>
            ))}
          </ul>
        ) : (
          <p key={i} className="whitespace-pre-line">
            {renderWithStatutes(block.lines.join("\n"))}
          </p>
        ),
      )}
    </div>
  );
}
